import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Cart Item Interface
 */
export interface CartItem {
  item_id: string;
  name: string;
  name_ar: string;
  price: number;
  quantity: number;
  image?: string;
  category?: string;
}

/**
 * Cart State Interface
 */
interface CartState {
  items: CartItem[];

  // Actions
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;

  // Computed
  getTotalPrice: () => number;
  getTotalItems: () => number;
  getItemCount: (itemId: string) => number;
}

/**
 * Cart Store with Zustand + localStorage persistence
 */
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      /**
       * Add item to cart (or increment quantity if exists)
       */
      addItem: (item) => {
        set((state) => {
          const existingItem = state.items.find(
            (i) => i.item_id === item.item_id,
          );

          if (existingItem) {
            // Item exists, increment quantity
            return {
              items: state.items.map((i) =>
                i.item_id === item.item_id
                  ? { ...i, quantity: i.quantity + 1 }
                  : i,
              ),
            };
          } else {
            // New item, add with quantity 1
            return {
              items: [...state.items, { ...item, quantity: 1 }],
            };
          }
        });
      },

      /**
       * Remove item from cart
       */
      removeItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.item_id !== itemId),
        }));
      },

      /**
       * Update item quantity
       */
      updateQuantity: (itemId, quantity) => {
        if (quantity <= 0) {
          // Remove item if quantity is 0 or less
          get().removeItem(itemId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.item_id === itemId ? { ...item, quantity } : item,
          ),
        }));
      },

      /**
       * Clear entire cart
       */
      clearCart: () => {
        set({ items: [] });
      },

      /**
       * Get total price
       */
      getTotalPrice: () => {
        return get().items.reduce(
          (total, item) => total + item.price * item.quantity,
          0,
        );
      },

      /**
       * Get total number of items
       */
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      /**
       * Get count of specific item
       */
      getItemCount: (itemId) => {
        const item = get().items.find((i) => i.item_id === itemId);
        return item ? item.quantity : 0;
      },
    }),
    {
      name: "idworks-cart-storage", // localStorage key
      version: 1,
    },
  ),
);
